// Pure, Firebase-free helpers for the Property Intelligence UI (same reason
// buyerStrategyCards.js and pricingStrategyFormatting.js are separate: they
// stay unit-testable without the app's Firebase initialization).

export const NAVY = "#16243c";
export const BLUE = "#2185d0";
export const LIGHT_BLUE_BG = "#eaf4fc";
export const LIGHT_BLUE_BORDER = "#cfe4f7";
export const LIGHT_GREY_BG = "#f4f7fa";
export const GREY_TEXT = "#767676";

export const PI_THEME = {
  icon: "search",
  color: "#2185d0",
  bg: "#eaf4fc",
  label: "Property Intelligence",
};

export const EVIDENCE_STATUS = {
  verified: { label: "Verified", color: "green", icon: "check circle" },
  conflicting_records: { label: "Conflicting Records", color: "orange", icon: "exchange" },
  potential_risk: { label: "Potential Risk", color: "yellow", icon: "warning sign" },
  no_matching_records: { label: "No Matching Records Found", color: "grey", icon: "search minus" },
  source_unavailable: { label: "Source Unavailable", color: "grey", icon: "unlink" },
  manual_verification_required: { label: "Manual Verification Required", color: "blue", icon: "clipboard check" },
  not_investigated: { label: "Not Investigated", color: "grey", icon: "minus circle" },
};

export function evidenceStatus(status) {
  return EVIDENCE_STATUS[status] || { label: "Unclassified", color: "grey", icon: "question circle" };
}

export const REPORT_STATUS_LABELS = {
  preparing: "Preparing",
  researching: "Researching",
  writing: "Writing the report",
  completed: "Completed",
  partially_completed: "Partially completed",
  failed_recoverable: "Needs attention",
};

const IN_PROGRESS = ["preparing", "researching", "writing"];
export function isResearchInProgress(status) {
  return IN_PROGRESS.includes(status);
}
export function isResearchFinished(status) {
  return status === "completed" || status === "partially_completed";
}

export const MODULE_TITLES = {
  propertyRecords: "Property records",
  gisHazards: "Official hazard maps",
  historyPermits: "History & permits",
  hoaMetroTaxes: "HOA, metro districts & taxes",
  amenities: "Community amenities",
  geologyEnvironment: "Structural, geological & environmental",
  insurance: "Insurance considerations",
  neighborhood: "Neighborhood, transportation & noise",
  publicSafety: "Police & code records",
  additionalConsiderations: "Additional considerations",
  agentConcerns: "Your requested research",
  narrative: "Writing the full report",
  clientSummary: "Drafting the client summary",
};
const MODULE_ORDER = Object.keys(MODULE_TITLES);

const DONE = ["succeeded", "failed", "skipped_budget"];

/** Ordered progress steps from the modules map returned by getPropertyIntelligence. */
export function researchProgress(modules = {}) {
  const steps = MODULE_ORDER.filter((key) => modules[key] && modules[key].status !== "superseded").map((key) => ({
    key,
    title: MODULE_TITLES[key],
    status: modules[key].status,
    error: modules[key].error || null,
  }));
  const done = steps.filter((s) => DONE.includes(s.status)).length;
  // The plan isn't fully known until phase 1 finishes; count the writing steps up front.
  const expected = Math.max(steps.length, 4) + (modules.narrative ? 0 : 1) + (modules.clientSummary ? 0 : 1);
  return { steps, done, total: expected, percent: Math.min(99, Math.round((done / expected) * 100)) };
}

// Agent-facing stages, each an aggregation of persisted backend modules
// (functions/propertyIntelligence/constants.js MODULES).
export const RESEARCH_STAGES = [
  { key: "records", title: "Property Records", description: "Transaction, MLS and county parcel records", icon: "file alternate outline", modules: ["propertyRecords"] },
  { key: "hazards", title: "Official Hazard Maps", description: "FEMA flood, USDA soil and wildfire maps", icon: "map outline", modules: ["gisHazards"] },
  { key: "history", title: "Property History, Permits & HOA", description: "Permits, sales history, HOA, districts, taxes and amenities", icon: "history", modules: ["historyPermits", "hoaMetroTaxes", "amenities"] },
  { key: "neighborhood", title: "Neighborhood & Insurance Research", description: "Geology, insurance, neighborhood, public records and your requested research", icon: "shield alternate", modules: ["geologyEnvironment", "insurance", "neighborhood", "publicSafety", "additionalConsiderations", "agentConcerns"] },
  { key: "report", title: "Detailed Analysis & Report", description: "Writing the full report and client summary", icon: "book", modules: ["narrative", "clientSummary"] },
];

export const STAGE_STATUS_DISPLAY = {
  pending: { label: "Pending", color: "#8a92a0", bg: "#f0f2f5", icon: "circle outline" },
  in_progress: { label: "In Progress", color: "#2185d0", bg: "#eaf4fc", icon: "circle notched" },
  complete: { label: "Complete", color: "#21ba45", bg: "#e9f9ee", icon: "check circle" },
  partial: { label: "Partial", color: "#c26a00", bg: "#fff4e5", icon: "adjust" },
  unavailable: { label: "Unavailable", color: "#767676", bg: "#f0f2f5", icon: "minus circle" },
  failed: { label: "Failed", color: "#db2828", bg: "#fdecec", icon: "times circle" },
};

function stageStatus(statuses) {
  if (statuses.length === 0) return "pending";
  if (statuses.some((s) => s === "running")) return "in_progress";
  const terminal = statuses.filter((s) => DONE.includes(s));
  if (terminal.length < statuses.length) {
    // Some modules still queued/waiting: in progress once any has finished, else pending.
    return terminal.length > 0 ? "in_progress" : "pending";
  }
  const ok = statuses.filter((s) => s === "succeeded").length;
  if (ok === statuses.length) return "complete";
  if (ok > 0) return "partial";
  return statuses.some((s) => s === "failed") ? "failed" : "unavailable";
}

/**
 * Stage list and progress derived ONLY from persisted module states.
 * `percent` is null (indeterminate) until the research plan exists - the
 * number of research tasks isn't known before then - and never reaches
 * 100 until the report itself is saved (reportStatus completed/partial).
 */
export function researchStages(modules = {}, reportStatus) {
  const live = Object.fromEntries(Object.entries(modules).filter(([, m]) => m && m.status !== "superseded"));
  const reportSaved = isResearchFinished(reportStatus);
  const stages = RESEARCH_STAGES.map((stage) => {
    const statuses = stage.modules.filter((k) => live[k]).map((k) => live[k].status);
    let status = stageStatus(statuses);
    // The final stage is only complete once the report is actually persisted.
    if (stage.key === "report" && status === "complete" && !reportSaved) status = "in_progress";
    if (stage.key === "report" && reportSaved && statuses.length === 0) status = "complete";
    return { ...stage, status, moduleCount: statuses.length, doneCount: statuses.filter((s) => DONE.includes(s)).length };
  });

  const planned = ["history", "neighborhood"].some((k) => stages.find((s) => s.key === k).moduleCount > 0);
  let percent = null;
  if (reportSaved) percent = 100;
  else if (planned) {
    const tracked = Object.values(live);
    const done = tracked.filter((m) => DONE.includes(m.status)).length;
    // +2 for writing modules not created yet; capped below 100 until saved.
    const total = tracked.length + (live.narrative ? 0 : 1) + (live.clientSummary ? 0 : 1);
    percent = Math.min(95, Math.round((done / total) * 100));
  }
  const anyFailed = Object.values(live).some((m) => m.status === "failed");
  return { stages, percent, anyFailed };
}

export function failedSteps(modules = {}) {
  return researchProgress(modules).steps.filter((s) => s.status === "failed");
}

// ---- report views (apply agent edits on top of original findings) ----

export function visibleFindings(report, { modules = null, forClient = false } = {}) {
  const edits = report?.agentEdits?.findings || {};
  return (report?.findings || [])
    .filter((f) => !modules || modules.includes(f.module))
    .filter((f) => !forClient || !edits[f.id]?.hiddenFromClient)
    .map((f) => ({ ...f, edit: edits[f.id] || null }));
}

export function recordsOfType(report, types) {
  const edits = report?.agentEdits?.findings || {};
  return (report?.records || []).filter((r) => types.includes(r.recordType)).map((r) => ({ ...r, edit: edits[r.id] || null }));
}

export function factRows(report) {
  const overrides = report?.agentEdits?.facts || {};
  return Object.entries(report?.facts || {})
    .filter(([, f]) => f.value !== null && f.value !== undefined && typeof f.value !== "object")
    .map(([field, f]) => ({
      field,
      label: f.label,
      unit: f.unit,
      value: f.value,
      source: f.sourceLabel,
      basis: f.basis,
      status: f.status,
      conflict: f.conflict,
      otherValues: Object.entries(f.values || {}).filter(([s]) => s !== f.source).map(([, v]) => v),
      override: overrides[field] || null,
    }));
}

export function formatFactValue(value, unit) {
  if (typeof value === "number" && unit) return `${value.toLocaleString("en-US")} ${unit}`;
  return String(value);
}

/** Joins text fragments as sentences without doubling punctuation. */
export function joinSentences(parts) {
  const clean = parts.filter(Boolean).map((p) => String(p).trim().replace(/[.\s]+$/, "")).filter(Boolean);
  return clean.length ? `${clean.join(". ")}.` : "";
}

export function sourcesFor(report, ids = []) {
  const all = report?.sources || {};
  return ids.map((id) => all[id]).filter(Boolean);
}

/** Narrative sections with any agent rewrite applied in place of the original text. */
export function narrativeSections(report) {
  const edits = report?.agentEdits?.sections || {};
  return (report?.narrative?.sections || []).map((s) => (edits[s.key] ? { ...s, paragraphs: edits[s.key].paragraphs, edited: edits[s.key] } : s));
}

// ---- dashboard card ----

export function buildPropertyIntelligenceCard({ pointer, isCurrent, onAction, restricted, restrictedMessage }) {
  const status = pointer?.status;
  const base = {
    key: "propertyIntelligence",
    theme: PI_THEME,
    description: "Permits, HOA & metro districts, property risks, insurance and neighborhood research.",
  };
  if (restricted) return { ...base, status: "restricted", restrictedMessage };
  if (isResearchFinished(status)) {
    return { ...base, status: "generated", actionLabel: "View Property Intelligence", onAction };
  }
  if (isResearchInProgress(status)) {
    return { ...base, status: "not_generated", features: ["Research in progress"], actionLabel: "View Progress", onAction };
  }
  if (status === "failed_recoverable") {
    return { ...base, status: "not_generated", features: ["Research needs attention"], actionLabel: "View Property Intelligence", onAction };
  }
  if (!isCurrent) return { ...base, status: "restricted", restrictedMessage: "No Property Intelligence was researched for this property." };
  return {
    ...base,
    status: "not_generated",
    features: ["Property facts & permits", "HOA & metro districts", "Risks, insurance & neighborhood"],
    actionLabel: "Research Property",
    onAction,
  };
}

// Research areas the report covers (Buyer property card chips). Informational
// only - a chip never implies anything was found in that area.
export const PI_COVERAGE = [
  { key: "permits", label: "Permits & Code", icon: "file alternate outline" },
  { key: "hoa", label: "HOA & Metro Districts", icon: "users" },
  { key: "risks", label: "Property Risks", icon: "warning sign" },
  { key: "insurance", label: "Insurance", icon: "shield alternate" },
  { key: "neighborhood", label: "Neighborhood Insights", icon: "map marker alternate" },
];

/**
 * What the Buyer property card's Property Intelligence panel shows, read
 * straight from the saved pointer status (backend REPORT_STATUS) - no
 * statuses of its own. Every action opens the existing Property Intelligence
 * flow (unlock modal, progress view or saved report); nothing here starts
 * research or uses a credit by itself.
 */
export function propertyIntelligencePanel({ pointer, isCurrent, restricted, restrictedMessage }) {
  const status = pointer?.status;
  if (isResearchFinished(status)) {
    const partial = status === "partially_completed";
    return {
      state: partial ? "partial" : "completed",
      statusLabel: partial ? "Partially completed" : "Generated",
      tone: partial ? "caution" : "positive",
      message: partial ? "Some research areas couldn't be completed - the report lists what to verify." : null,
      actionLabel: "View Property Intelligence Report",
    };
  }
  if (isResearchInProgress(status)) {
    return {
      state: "generating",
      statusLabel: "Research in progress",
      tone: "info",
      message: "Research takes a few minutes and is saved to this transaction. You can leave this page.",
      actionLabel: "View Progress",
    };
  }
  if (status === "failed_recoverable") {
    return {
      state: "failed",
      statusLabel: "Needs attention",
      tone: "negative",
      message: "Some research steps didn't finish. Retrying continues where it stopped.",
      actionLabel: "Review & Retry",
    };
  }
  if (restricted) return { state: "restricted", statusLabel: null, tone: "neutral", message: restrictedMessage || null, actionLabel: null };
  if (!isCurrent) return { state: "none", statusLabel: null, tone: "neutral", message: "No Property Intelligence report was created for this property.", actionLabel: null };
  return {
    state: "not_generated",
    statusLabel: "Not generated",
    tone: "neutral",
    message: "Research is available for this property. The report is saved to this transaction for you to review, edit and share.",
    actionLabel: "Generate Property Intelligence",
  };
}

// ---- intake ----

export const PI_PROPERTY_TYPE_OPTIONS = [
  { key: "sfd", text: "Single Family (detached)", value: "Single Family Detached" },
  { key: "townhome", text: "Townhome", value: "Townhome" },
  { key: "condo", text: "Condo", value: "Condo" },
  { key: "duplex", text: "Duplex / half duplex", value: "Duplex" },
  { key: "multi", text: "Multi-family (2-4 units)", value: "Multi-Family" },
  { key: "manufactured", text: "Manufactured / mobile", value: "Manufactured Home" },
  { key: "other", text: "Other", value: "Other" },
];

const str = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

export function defaultPropertyInputs(transaction) {
  const pd = transaction?.propertyDetails || {};
  const baths = Number.isFinite(pd.bathroomsFull)
    ? pd.bathroomsFull + (pd.bathroomsThreeQuarter || 0) * 0.75 + (pd.bathroomsHalf || 0) * 0.5
    : null;
  const finishedBelow = Number(pd.belowGradeFinishedSqft) || 0;
  const unfinishedBelow = Number(pd.belowGradeUnfinishedSqft) || 0;
  return {
    propertyType: str(pd.propertySubType || pd.propertyType),
    yearBuilt: str(pd.yearBuilt),
    totalBuildingSqft: "",
    finishedSqft: pd.aboveGradeFinishedSqft && pd.belowGradeFinishedSqft !== undefined && pd.belowGradeFinishedSqft !== null ? str(Number(pd.aboveGradeFinishedSqft) + finishedBelow) : "",
    aboveGradeSqft: str(pd.aboveGradeFinishedSqft),
    basementSqft: finishedBelow || unfinishedBelow ? str(finishedBelow + unfinishedBelow) : "",
    basementFinishedSqft: str(pd.belowGradeFinishedSqft),
    bedrooms: str(pd.bedroomsTotal),
    bathrooms: baths ? str(baths) : "",
    garageSpaces: str(pd.garageSpaces),
    lotSizeSqft: str(pd.lotSizeSqft),
    otherCharacteristics: "",
  };
}

/** Which inputs came prefilled from the transaction/MLS (shown as such in the form). */
export function prefilledFields(transaction) {
  const defaults = defaultPropertyInputs(transaction);
  return new Set(Object.entries(defaults).filter(([, v]) => v !== "").map(([k]) => k));
}

// ---- sharing ----

export function addressLine(address = {}) {
  const street = [address.street, address.unit].filter(Boolean).join(" ");
  return [street, address.city, [address.state, address.zipcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

export function portalLoginUrl(env = process.env) {
  return `${env.REACT_APP_ENVIRONMENT === "development" ? "http://localhost:3000" : "https://app.transactioner.com"}/partyLogin`;
}

/**
 * Prefill object for the existing email composer (emailSlice
 * fetchEmailFromTemplate). Never sends anything.
 */
export function buildClientEmail({ address, summary, recipients, currentUserProfile, portalLink, pdfShared }) {
  const firstNames = recipients.map((r) => r.firstName).filter(Boolean);
  const greeting = firstNames.length ? `Hi ${firstNames.join(" and ")},` : "Hello,";
  const included = (summary?.sections || []).filter((s) => s.include && s.body && s.body.trim());
  const body = included.map((s) => `${s.title.toUpperCase()}\n${s.body.trim()}`).join("\n\n");
  const parts = [greeting];
  if (summary?.personalNote?.trim()) parts.push(summary.personalNote.trim());
  else parts.push(`I researched ${address} and wanted to share a summary of what I found.`);
  if (body) parts.push(body);
  if (pdfShared && portalLink) {
    parts.push(`The full Property Intelligence Report is in the Documents section of your TransActioner client portal: ${portalLink}`);
  }
  parts.push("This summary is based on public records and other sources available at the time of research. It isn't a home inspection, survey or appraisal - please let me know if you have any questions.");
  const signature = currentUserProfile?.emailSigField || "";
  return {
    subject: `Property Intelligence Review — ${address}`,
    message: `${parts.join("\n\n")}\n\n${signature}`.trimEnd(),
    from: `${currentUserProfile?.firstName || ""} ${currentUserProfile?.lastName || ""}`.trim(),
    to: [...new Set(recipients.map((r) => r.role))],
    cc: [],
    bcc: [],
    otherEmailsTo: "",
    otherEmailsCc: "",
    otherEmailsBcc: "",
  };
}
